/**
 * UI-6c Task 8 — useCsatSummary hook.
 *
 * Read-only hook over GET `/csat/summary?by={agent|queue|day}&from=&to=`.
 * Returns the dimensioned `{ buckets, overall }` shape per spec §3.3
 * (apps/api/src/routes/reports/csat.ts).
 *
 * Per plan §0.A, the `by` dimension is `'agent' | 'queue' | 'day'` only —
 * `'channel'` is deferred to Phase 2 (the `csat_surveys` table has no
 * channel column and `calls` has no clean join through).
 *
 * The hook deliberately skips fetching while `by` is undefined: the legacy
 * path of `/csat/summary` (no `by` param) returns a different `{ rows }`
 * shape used by the CsatScore widget. Returning `null` until the consumer
 * picks a dimension keeps that fork out of this hook entirely.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface CsatSummaryBucket {
  key: string
  keyLabel: string
  avgScore: number
  responseCount: number
  firstCallResolutionRate: number
}

export interface CsatSummary {
  buckets: CsatSummaryBucket[]
  overall: {
    avgScore: number
    responseCount: number
    firstCallResolutionRate: number
  }
  scale: number
}

export interface UseCsatSummaryParams {
  by?: 'agent' | 'queue' | 'day'
  from?: Date
  to?: Date
}

export interface UseCsatSummaryResult {
  summary: CsatSummary | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

function buildPath(by: 'agent' | 'queue' | 'day', from?: Date, to?: Date): string {
  const params = new URLSearchParams()
  params.set('by', by)
  if (from) params.set('from', from.toISOString())
  if (to) params.set('to', to.toISOString())
  return `/csat/summary?${params.toString()}`
}

export function useCsatSummary(params: UseCsatSummaryParams): UseCsatSummaryResult {
  const { by, from, to } = params
  const [summary, setSummary] = useState<CsatSummary | null>(null)
  const [loading, setLoading] = useState<boolean>(by !== undefined)
  const [error, setError] = useState<string | null>(null)

  // Serialise dates to primitive keys for the effect deps array so a new
  // `Date` instance with the same millisecond value doesn't trigger a
  // spurious refetch.
  const fromKey = from ? from.toISOString() : ''
  const toKey = to ? to.toISOString() : ''

  const fetchOnce = useCallback(async (): Promise<void> => {
    if (by === undefined) {
      setSummary(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<CsatSummary>(buildPath(by, from, to))
      setSummary(resp)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load CSAT summary')
      setSummary(null)
    } finally {
      setLoading(false)
    }
    // `from` / `to` are captured via the serialised keys in the effect
    // below; deps here intentionally mirror the effect so refetch() always
    // matches the most recently rendered params.
  }, [by, fromKey, toKey])

  useEffect(() => {
    let cancelled = false
    if (by === undefined) {
      setSummary(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    api
      .get<CsatSummary>(buildPath(by, from, to))
      .then((resp) => {
        if (!cancelled) setSummary(resp)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load CSAT summary')
          setSummary(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return (): void => {
      cancelled = true
    }
  }, [by, fromKey, toKey])

  return { summary, loading, error, refetch: fetchOnce }
}
